import { createFileRoute, Link } from '@tanstack/react-router'
import { toast } from 'sonner'

import {
  useAdminOrganizationDetail,
  useAdminRemoveUserFromOrganization,
  useAdminAddUserRole,
  useAdminRemoveUserRole,
} from '@/features/admin/services/admin.convex'
import { useConfirmDialog } from '@/contexts/confirm-dialog-context'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AvatarWithInitials } from '@/components/common/avatar-with-initials'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/components/ui/multi-select'

const ORG_ROLES = [
  'admin',
  'manager',
  'member',
  'analytics',
  'billing',
] as const

export const Route = createFileRoute(
  '/(dashboard)/admin/organizations/$organizationId/members/',
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    name: (search.name as string) || undefined,
  }),
  beforeLoad: ({ params, search }) => ({
    breadcrumbs: [
      { title: 'Admin' },
      { title: 'Organizations', href: '/admin/organizations' },
      { title: search.name || params.organizationId },
      { title: 'Members' },
    ],
  }),
})

function RouteComponent() {
  const { organizationId } = Route.useParams()
  const { showConfirmDialog } = useConfirmDialog()

  const { data: org } = useAdminOrganizationDetail(organizationId)
  const removeUser = useAdminRemoveUserFromOrganization()
  const addRole = useAdminAddUserRole()
  const removeRole = useAdminRemoveUserRole()

  const handleRemoveUser = (userId: string, name: string) => {
    showConfirmDialog({
      title: 'Remove Member',
      description: `Remove "${name}" from this organization?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        try {
          await removeUser({
            userId: userId as never,
            organizationId: organizationId as never,
          })
          toast.success(`${name} removed`)
        } catch {
          toast.error('Failed to remove member')
        }
      },
    })
  }

  const handleRoleChange = async (memberId: string, newKeys: string[]) => {
    if (!org) return
    const member = org.members.find((m) => m?.id === memberId)
    if (!member) return
    const currentKeys = member.roles.map((r) => r.key)
    const toAdd = newKeys.filter((k) => !currentKeys.includes(k))
    const toRemove = currentKeys.filter((k) => !newKeys.includes(k))
    try {
      await Promise.all([
        ...toAdd.map((k) =>
          addRole({
            userId: memberId as never,
            organizationId: organizationId as never,
            roleKey: k,
          }),
        ),
        ...toRemove.map((k) =>
          removeRole({
            userId: memberId as never,
            organizationId: organizationId as never,
            roleKey: k,
          }),
        ),
      ])
    } catch {
      toast.error('Failed to update roles')
    }
  }

  if (!org) return null

  return (
    <div className="bg-muted/30 rounded-lg p-6">
      <div className="relative w-full overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-muted/50 border-b transition-colors">
              <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                User
              </TableHead>
              <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                Role
              </TableHead>
              <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                Member Since
              </TableHead>
              <TableHead className="text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {org.members.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="p-2 align-middle text-center text-muted-foreground py-12"
                >
                  No members yet
                </TableCell>
              </TableRow>
            )}
            {org.members.map((member) => {
              if (!member) return null
              return (
                <TableRow
                  key={member.id}
                  className="hover:bg-muted/50 border-b transition-colors"
                >
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <AvatarWithInitials
                        name={member.name ?? member.email ?? 'Unknown'}
                        src={member.image ?? undefined}
                        className="h-8 w-8"
                      />
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {member.name ?? 'Unnamed'}
                          {member.isPlatformAdmin && (
                            <Badge variant="default" className="text-xs">
                              Platform Admin
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {member.email}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="p-2 align-middle">
                    <MultiSelect
                      values={member.roles.map((r) => r.key)}
                      onValuesChange={(keys) =>
                        handleRoleChange(member.id, keys)
                      }
                    >
                      <MultiSelectTrigger className="h-8 min-w-36 bg-background text-sm">
                        <MultiSelectValue placeholder="No roles" />
                      </MultiSelectTrigger>
                      <MultiSelectContent>
                        <MultiSelectGroup>
                          {ORG_ROLES.map((r) => (
                            <MultiSelectItem key={r} value={r}>
                              {r.charAt(0).toUpperCase() + r.slice(1)}
                            </MultiSelectItem>
                          ))}
                        </MultiSelectGroup>
                      </MultiSelectContent>
                    </MultiSelect>
                  </TableCell>
                  <TableCell className="p-2 align-middle whitespace-nowrap text-sm text-muted-foreground">
                    {new Date(member.memberSince).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="p-2 align-middle whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link
                          to="/admin/users/$userId/organizations"
                          params={{ userId: member.id }}
                          search={{ name: member.name ?? undefined }}
                        >
                          View User
                        </Link>
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() =>
                          handleRemoveUser(
                            member.id,
                            member.name ?? member.email ?? 'User',
                          )
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
